import { createContext, useContext } from 'react';
import type { FeatureFlagPath } from '@/constants/features';
import type { FeatureConfigContextType, FeatureFlagConfig, FeatureFlagMap } from './types';

export const FeatureConfigContext = createContext<FeatureConfigContextType | undefined>(undefined);

export const useFeatureConfig = (): FeatureConfigContextType => {
    const context = useContext(FeatureConfigContext);
    if (context === undefined) {
        throw new Error('useFeatureConfig must be used within a FeatureConfigProvider');
    }
    return context;
}

/**
 * 解析功能标记路径，支持模块级配置和具体功能配置
 */
function resolveFeatureFlag(
    path: string,
    featureFlags: FeatureFlagMap,
    modules?: Record<string, FeatureFlagConfig | undefined>
): boolean {
    // 直接检查功能标记
    if (featureFlags[path] !== undefined) {
        return Boolean(featureFlags[path]);
    }

    // 解析点号分隔的路径
    const parts = path.split('.');
    if (parts.length >= 2 && modules) {
        const [moduleName, ...featureParts] = parts;
        const moduleConfig = modules[moduleName];
        
        if (moduleConfig) {
            // 检查模块级开关
            if (moduleConfig.enabled !== undefined) {
                return Boolean(moduleConfig.enabled);
            }
            
            // 检查具体功能配置
            const featureName = featureParts.join('.');
            if (moduleConfig[featureName] !== undefined) {
                return Boolean(moduleConfig[featureName]);
            }
        }
    }

    return false;
}

/**
 * 功能可见性检查，支持新的路径格式
 */
export const useFeatureVisibility = (key: FeatureFlagPath | string): boolean => {
    const { featureFlags, showAllFeatures, modules } = useFeatureConfig();
    if (showAllFeatures) return true;

    return resolveFeatureFlag(key, featureFlags, modules);
}

/**
 * 显式功能标记检查，不受 showAllFeatures 影响
 * 用于需要严格按配置控制的功能（如仅在 dtest 环境开启的实验性功能）
 */
export const useExplicitFeatureFlag = (key: FeatureFlagPath | string): boolean => {
    const { featureFlags, modules } = useFeatureConfig();
    return resolveFeatureFlag(key, featureFlags, modules);
}

/**
 * 批量检查多个功能标记
 */
export const useMultipleFeatureVisibility = (keys: (FeatureFlagPath | string)[]): Record<string, boolean> => {
    const { featureFlags, showAllFeatures, modules } = useFeatureConfig();
    
    return keys.reduce((acc, key) => {
        acc[key] = showAllFeatures || resolveFeatureFlag(key, featureFlags, modules);
        return acc;
    }, {} as Record<string, boolean>);
}

/**
 * 检查整个模块是否启用
 */
export const useModuleVisibility = (moduleName: string): boolean => {
    const { showAllFeatures, modules } = useFeatureConfig();
    if (showAllFeatures) return true;

    const moduleConfig = modules?.[moduleName];
    return moduleConfig?.enabled ?? false;
}
